import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBusinessAuth } from '../../contexts/BusinessAuthContext';
import './CustomerDetail.css';

const CustomerDetail = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { token } = useBusinessAuth();
  const [customerData, setCustomerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:10000';

  useEffect(() => {
    fetchCustomerDetail();
  }, [customerId]);

  const fetchCustomerDetail = async () => {
    try {
      const response = await fetch(`${API_URL}/api/daycare/customer/${customerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer details');
      }

      const data = await response.json();
      setCustomerData(data);
    } catch (err) {
      console.error('Error fetching customer details:', err);
      setError('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProductName = (productType) => {
    const names = {
      single_video: 'Single Video',
      video_3pack: '3-Video Pack',
      video_10pack: '10-Video Pack',
    };
    return names[productType] || productType;
  };

  const getStatusColor = (status) => {
    const colors = {
      complete: 'green',
      processing: 'orange',
      failed: 'red',
      pending: 'gray',
      completed: 'green',
    };
    return colors[status] || 'gray';
  };

  if (loading) {
    return (
      <div className="customer-detail-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading customer details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="customer-detail-container">
        <div className="error-state">
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={fetchCustomerDetail} className="retry-button">
              Retry
            </button>
            <button onClick={() => navigate('/customers')} className="back-button">
              Back to Customers
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!customerData) {
    return null;
  }

  const { customer, stats, payments, videos } = customerData;

  return (
    <div className="customer-detail-container">
      <div className="customer-detail-header">
        <button onClick={() => navigate('/customers')} className="back-button">
          ← Back to Customers
        </button>
        <button onClick={fetchCustomerDetail} className="refresh-button">
          Refresh
        </button>
      </div>

      {/* Customer Info Card */}
      <div className="customer-info-card">
        <div className="customer-avatar">
          <div className="avatar-circle">
            {customer.name.charAt(0).toUpperCase()}
          </div>
        </div>
        <div className="customer-info">
          <h1>{customer.name}</h1>
          <div className="customer-contact">
            <p className="email">{customer.email}</p>
            {customer.phone && <p className="phone">{customer.phone}</p>}
          </div>
          <p className="joined-date">
            Joined {formatDate(customer.joinedDate)}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card total-spent">
          <div className="stat-icon">💰</div>
          <div className="stat-content">
            <div className="stat-value">{formatCurrency(stats.totalSpent)}</div>
            <div className="stat-label">Total Spent</div>
          </div>
        </div>

        <div className="stat-card credits">
          <div className="stat-icon">🎟️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalCredits}</div>
            <div className="stat-label">Credits Purchased</div>
          </div>
        </div>

        <div className="stat-card used-credits">
          <div className="stat-icon">✓</div>
          <div className="stat-content">
            <div className="stat-value">{stats.usedCredits}</div>
            <div className="stat-label">Credits Used</div>
          </div>
        </div>

        <div className="stat-card remaining-credits">
          <div className="stat-icon">🎬</div>
          <div className="stat-content">
            <div className="stat-value">{stats.remainingCredits}</div>
            <div className="stat-label">Credits Remaining</div>
          </div>
        </div>

        <div className="stat-card videos">
          <div className="stat-icon">📹</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalVideos}</div>
            <div className="stat-label">Videos Created</div>
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="section">
        <h2>Payment History</h2>
        {payments.length === 0 ? (
          <div className="no-data">No payment history</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Product</th>
                  <th>Amount</th>
                  <th>Credits Purchased</th>
                  <th>Credits Used</th>
                  <th>Credits Left</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id}>
                    <td>{formatDate(payment.date)}</td>
                    <td>{getProductName(payment.productType)}</td>
                    <td className="amount-cell">{formatCurrency(payment.amount)}</td>
                    <td>{payment.creditsPurchased}</td>
                    <td>{payment.creditsUsed}</td>
                    <td>
                      <span className={`credits-badge ${payment.creditsLeft === 0 ? 'no-credits' : ''}`}>
                        {payment.creditsLeft}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge status-${payment.status}`}>
                        {payment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Video History */}
      <div className="section">
        <h2>Video History</h2>
        {videos.length === 0 ? (
          <div className="no-data">No videos created yet</div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Job ID</th>
                  <th>Pet Name</th>
                  <th>Status</th>
                  <th>Video</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video) => (
                  <tr key={video.id}>
                    <td>{formatDate(video.createdAt)}</td>
                    <td className="job-id">{video.jobId}</td>
                    <td className="pet-name">{video.petName || '-'}</td>
                    <td>
                      <span className={`status-badge status-${video.status}`}>
                        {video.status}
                      </span>
                    </td>
                    <td>
                      {video.videoUrl ? (
                        <a
                          href={video.videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="video-link"
                        >
                          View Video
                        </a>
                      ) : (
                        <span className="no-video">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDetail;
